import { useEffect, useCallback } from 'react';
import { Resource, Action } from '../../../Constants/socket';
import useSocketEvent from '../../../Hooks/useSocketEvent';
import useSocket from '../../../Hooks/useSocket';

/**
 * Hook to listen for conversation-related events on the socket
 * and update the conversations accordingly.
 * 
 * @param {Object} params - Parameters object
 * @param {Function} params.setConversations - Function to update conversations
 * @param {Object|null} params.selectedConversation - Currently selected conversation
 * @param {Function} params.setSelectedConversation - Function to update selected conversation
 * @returns {boolean} Whether the socket is connected
 */
export default function useConversationSocketEvents({
  setConversations,
  selectedConversation,
  setSelectedConversation
}) {
  const { socket, connectionState } = useSocket();
  const isConnected = connectionState.status === 'connected';
  // Handle conversation create event
  const handleConversationCreate = useCallback((data) => {
    if (!data) return;

    setConversations(prevConversations => {
      // Check if conversation already exists
      const exists = prevConversations.some(conv => conv.id === data.id);
      if (exists) return prevConversations;

      // Add new conversation to the beginning of the list
      return [data, ...prevConversations];
    });

    // After receiving a new conversation, subscribe to its updates
    // from the time it was created to ensure we don't miss events
    if (isConnected && socket && data.id) {
      socket.subscribeToResource(Resource.CONVERSATION, [data.id], data.created_at);
    }
  }, [setConversations]);

  // Handle conversation update event - OPTIMIZED for performance
  const handleConversationUpdate = useCallback((data) => {
    if (!data) return;

    // Update in the conversations list
    setConversations(prevConversations => {
      let hasChanges = false;
      const updatedConversations = prevConversations.map(conv => {
        if (conv.id !== data.id) return conv;

        let convHasChanges = false;
        const updatedConv = { ...conv };

        // Only update fields that actually changed
        Object.keys(data).forEach(key => {
          if (conv[key] !== data[key]) {
            updatedConv[key] = data[key];
            convHasChanges = true;
          }
        });

        if (convHasChanges) {
          hasChanges = true;
          return updatedConv;
        }

        return conv;
      });

      // Return original array if no changes to prevent unnecessary re-renders
      return hasChanges ? updatedConversations : prevConversations;
    });

    // Update selected conversation if it matches - OPTIMIZED
    if (selectedConversation?.id === data.id) {
      setSelectedConversation(prev => {
        let hasChanges = false;
        const updated = { ...prev };

        // Only update fields that actually changed
        Object.keys(data).forEach(key => {
          if (prev[key] !== data[key]) {
            updated[key] = data[key];
            hasChanges = true;
          }
        });

        return hasChanges ? updated : prev;
      });
    }
  }, [setConversations, selectedConversation, setSelectedConversation]);

  // Handle conversation delete event
  const handleConversationDelete = useCallback((data) => {
    if (!data) return;

    setConversations(prevConversations => {
      const filteredConversations = prevConversations.filter(conv => conv.id !== data.id);

      // If the deleted conversation is the selected one, select another conversation or clear it
      if (selectedConversation?.id === data.id) {
        if (filteredConversations.length > 0) {
          // Find index of deleted conversation in the original array
          const deletedIndex = prevConversations.findIndex(conv => conv.id === data.id);

          // Try to select the next conversation or the previous one if we're at the end
          const nextIndex = deletedIndex < filteredConversations.length ? deletedIndex : deletedIndex - 1;
          // Make sure we have a valid index
          const newSelectedIndex = Math.max(0, Math.min(nextIndex, filteredConversations.length - 1));

          // Select the new conversation
          setTimeout(() => {
            if (filteredConversations[newSelectedIndex]) {
              setSelectedConversation(filteredConversations[newSelectedIndex]);
            }
          }, 0);
        } else {
          // No conversations left, clear selected conversation
          setSelectedConversation(null);
        }
      }

      return filteredConversations;
    });
  }, [setConversations, selectedConversation, setSelectedConversation]);


  // Set up socket event listeners
  useSocketEvent(Resource.CONVERSATION, Action.CREATE, handleConversationCreate);
  useSocketEvent(Resource.CONVERSATION, Action.UPDATE, handleConversationUpdate);
  useSocketEvent(Resource.CONVERSATION, Action.DELETE, handleConversationDelete);
  // Note: MESSAGE:CREATE listener is unnecessary because backend emits CONVERSATION:UPDATE
  // on message creation with updated last_message/updated_time. We already listen to
  // CONVERSATION:UPDATE and update both list and selected conversation accordingly.
} 